import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isNativePlatform, hapticImpact } from '@/lib/capacitor';
import { App as CapacitorApp } from '@capacitor/app';

export function useNativeAppBehaviors() {
  const location = useLocation();

  useEffect(() => {
    if (!isNativePlatform()) return;

    try {
      // Haptic feedback on route change
      hapticImpact('LIGHT').catch(() => {});

      // Scroll to top on route change
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error in route change handler:', error);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isNativePlatform()) return;

    let backButtonListener: any;
    let appStateListener: any;
    let urlOpenListener: any;

    const setupListeners = async () => {
      try {
        // Handle Android back button
        backButtonListener = await CapacitorApp.addListener('backButton', ({ canGoBack }) => {
          try {
            if (canGoBack) {
              window.history.back();
            } else {
              // On home page, minimize app instead of closing
              CapacitorApp.minimizeApp().catch(() => {
                // If minimize fails, just ignore
              });
            }
          } catch (error) {
            console.error('Error handling back button:', error);
          }
        });

        // Handle app state changes
        appStateListener = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
          try {
            if (isActive) {
              // App came to foreground - could refresh data
              console.log('App is active');
            }
          } catch (error) {
            console.error('Error handling app state change:', error);
          }
        });

        // Handle app URL open (deep linking)
        urlOpenListener = await CapacitorApp.addListener('appUrlOpen', (data) => {
          try {
            console.log('App opened with URL:', data.url);
            // Handle deep links here
          } catch (error) {
            console.error('Error handling URL open:', error);
          }
        });
      } catch (error) {
        console.error('Error setting up app listeners:', error);
      }
    };

    setupListeners();

    return () => {
      try {
        if (backButtonListener) backButtonListener.remove();
        if (appStateListener) appStateListener.remove();
        if (urlOpenListener) urlOpenListener.remove();
      } catch (error) {
        console.error('Error removing listeners:', error);
      }
    };
  }, []);
}

// Hook for adding native-like scroll behavior
export function useNativeScrollBehavior() {
  useEffect(() => {
    if (!isNativePlatform()) return;

    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // Snap to nearest section if needed
        const scrollTop = window.scrollY;
        if (scrollTop > 0 && scrollTop < 100) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);
}

// Hook for preventing overscroll bounce on certain elements
export function usePreventOverscroll(ref: React.RefObject<HTMLElement>) {
  useEffect(() => {
    if (!isNativePlatform()) return;

    const element = ref.current;
    if (!element) return;

    const handleTouchMove = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-scrollable]')) {
        return; // Allow scrolling in scrollable areas
      }
      
      // Prevent overscroll
      if (
        (element.scrollTop === 0 && e.touches[0].clientY > element.clientHeight / 2) ||
        (element.scrollTop + element.clientHeight >= element.scrollHeight &&
          e.touches[0].clientY < element.clientHeight / 2)
      ) {
        e.preventDefault();
      }
    };

    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => element.removeEventListener('touchmove', handleTouchMove);
  }, [ref]);
}

